// src/store/slices/authSlice.ts

import {
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type { User, SubscriptionTier } from "@/types/auth.types";

/**
 * Redux state for authentication
 *
 * This slice manages authentication state synced from AuthService.
 * It's the single source of truth for:
 * - User data (from Auth0 - no longer from backend)
 * - Access tokens (for API requests)
 * - Authentication status
 *
 * TODO: Future Auth0 metadata improvements
 * - Remove legacy profileLoading, profileError, lastFetched, cacheExpiry (no longer used)
 * - Implement Auth0 Management API integration for profile updates
 * - Add methods to update user_metadata (username, firstName, lastName)
 * - Add methods to update app_metadata (tier, subscription status) via backend Auth0 Actions
 */
interface AuthState {
  // Authentication status
  isAuthenticated: boolean;
  isLoading: boolean;

  // Unified user data (from auth provider + backend)
  user: User | null;

  // Access token management (centralized single source of truth)
  accessToken: string | null;
  tokenExpiry: number | null; // Unix timestamp in milliseconds

  // Backend profile loading state
  profileLoading: boolean;
  profileError: string | null;

  // Quick access to subscription info (denormalized from user)
  subscriptionTier: SubscriptionTier;
  isSubscriptionActive: boolean;

  // Caching metadata
  lastFetched: string | null; // ISO timestamp
  cacheExpiry: number; // minutes
}

const CACHE_DURATION_MINUTES = 30; // Cache for 30 minutes
const CACHE_KEY = "hermes_user_profile";

// Helper functions for localStorage persistence
const saveToCache = (user: User) => {
  try {
    const cacheData = {
      user,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn("Failed to save user to cache:", error);
  }
};

const loadFromCache = (): {
  user: User;
  timestamp: string;
} | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const parsed = JSON.parse(cached) as { user: User; timestamp: string };
    const cacheTime = new Date(parsed.timestamp);
    const now = new Date();
    const diffMinutes = (now.getTime() - cacheTime.getTime()) / (1000 * 60);

    // Check if cache is still valid
    if (diffMinutes <= CACHE_DURATION_MINUTES) {
      return parsed;
    } else {
      // Cache expired, remove it
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
  } catch (error) {
    console.warn("Failed to load user from cache:", error);
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
};

const clearCache = () => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.warn("Failed to clear profile cache:", error);
  }
};

// Load cached data for initial state
const cachedData = loadFromCache();

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: cachedData?.user || null,
  accessToken: null,
  tokenExpiry: null,
  profileLoading: false,
  profileError: null,
  subscriptionTier: cachedData?.user?.tier || "free",
  isSubscriptionActive: cachedData?.user?.subscriptionActive || false,
  lastFetched: cachedData?.timestamp || null,
  cacheExpiry: CACHE_DURATION_MINUTES,
};

/**
 * DEPRECATED: transformBackendProfile
 * We no longer fetch user profile from backend.
 * User data will come from Auth0 metadata in the future.
 */
// Commented out - no longer used
// const transformBackendProfile = (
//   profile: UserProfile,
//   existingUser: User | null
// ): User => { ... };

/**
 * DEPRECATED: fetchUserProfile
 * Removed backend dependency. User profile data will be fetched from Auth0 metadata.
 * For now, all user data comes from Auth0 directly via AuthService.
 */
// Commented out - no longer calling backend /users/profile
// export const fetchUserProfile = createAsyncThunk(...);

/**
 * DEPRECATED: refreshUserProfile
 * Removed backend dependency.
 */
// Commented out - no longer calling backend
// export const refreshUserProfile = createAsyncThunk(...);

/**
 * DEPRECATED: updateSubscription
 * Removed backend dependency. Subscription will be managed via Auth0 metadata.
 */
// Commented out - no longer calling backend /users/subscription
// export const updateSubscription = createAsyncThunk(...);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     * Set authenticated user (from AuthService)
     * This is the main way to sync AuthService state → Redux
     */
    setUser: (
      state,
      action: PayloadAction<{
        user: User | null;
        isAuthenticated: boolean;
        isLoading: boolean;
      }>
    ) => {
      state.user = action.payload.user;
      state.isAuthenticated = action.payload.isAuthenticated;
      state.isLoading = action.payload.isLoading;

      // Update denormalized subscription fields
      if (action.payload.user) {
        state.subscriptionTier = action.payload.user.tier;
        state.isSubscriptionActive = action.payload.user.subscriptionActive;
      }
    },

    /**
     * Merge backend profile data into existing user
     * Used when fetching additional profile data from backend
     */
    mergeUserProfile: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = {
          ...state.user,
          ...action.payload,
        };

        // Update denormalized fields
        if (action.payload.tier) {
          state.subscriptionTier = action.payload.tier;
        }
        if (action.payload.subscriptionActive !== undefined) {
          state.isSubscriptionActive = action.payload.subscriptionActive;
        }

        // Update cache
        saveToCache(state.user);
      }
    },

    /**
     * Set access token and expiry (centralized token management)
     */
    setAccessToken: (
      state,
      action: PayloadAction<{
        token: string;
        expiresIn: number; // Seconds until expiry
      }>
    ) => {
      state.accessToken = action.payload.token;
      // Calculate expiry timestamp (current time + expiresIn seconds)
      state.tokenExpiry = Date.now() + action.payload.expiresIn * 1000;
    },

    /**
     * Clear access token (useful for forced refresh scenarios)
     */
    clearAccessToken: (state) => {
      state.accessToken = null;
      state.tokenExpiry = null;
    },

    /**
     * Clear all auth state (for logout)
     */
    clearAuthState: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.accessToken = null;
      state.tokenExpiry = null;
      state.profileLoading = false;
      state.profileError = null;
      state.subscriptionTier = "free";
      state.isSubscriptionActive = false;
      state.lastFetched = null;
      // Clear cache on logout
      clearCache();
    },

    /**
     * Clear profile errors
     */
    clearProfileError: (state) => {
      state.profileError = null;
    },

    /**
     * Force cache invalidation
     */
    invalidateCache: (state) => {
      state.lastFetched = null;
      clearCache();
    },

    /**
     * Update cache expiry setting
     */
    setCacheExpiry: (state, action: PayloadAction<number>) => {
      state.cacheExpiry = action.payload;
    },
  },
  // extraReducers removed - no longer using fetchUserProfile or updateSubscription thunks
});

export const {
  setUser,
  mergeUserProfile,
  setAccessToken,
  clearAccessToken,
  clearAuthState,
  clearProfileError,
  invalidateCache,
  setCacheExpiry,
} = authSlice.actions;

export default authSlice.reducer;
